import React, { type FC, useContext } from 'react';
import type { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import { CardsContext } from '../../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import { BADGE_CONFIG, BADGE_SLOT_LIMITS } from './badgeRegistry';
import { SlotContainer } from './styled';
import type { BADGE_SLOTS, BadgeContext } from './types';

export type BadgeSlotProps = {
  name: BADGE_SLOTS | BADGE_SLOTS[];
  promotionTypes: PROMOTION_TYPES[];
  dealCard: DealCard;
  isOverlay?: boolean;
};

const toArray = <T,>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);

const unique = <T,>(items: T[]): T[] => [...new Set(items)];

// Registry order is user-visible priority: the first types in the slot win it.
export const getSlotTypes = (slot: BADGE_SLOTS, types: PROMOTION_TYPES[]): PROMOTION_TYPES[] => {
  const typesSet = new Set(types);
  return (Object.keys(BADGE_CONFIG[slot]) as PROMOTION_TYPES[])
    .filter((t) => typesSet.has(t))
    .slice(0, BADGE_SLOT_LIMITS[slot]);
};

const BadgeSlot: FC<BadgeSlotProps> = ({ name, promotionTypes, dealCard, isOverlay }) => {
  const { isWaiting } = useContext(CardsContext);

  if (isWaiting || !promotionTypes?.length) return null;

  const slots = toArray(name);
  const types = unique(slots.flatMap((s) => getSlotTypes(s, promotionTypes)));

  if (!types.length) return null;

  const badges = slots.reduce((acc, s) => ({ ...acc, ...BADGE_CONFIG[s] }), {} as Record<string, FC<BadgeContext>>);
  const slotLimit = BADGE_SLOT_LIMITS[slots[0]];

  return (
    <SlotContainer $slot={slots[0]}>
      {types.map((type, index) => {
        const Badge = badges[type];
        const ctx: BadgeContext = { dealCard, isOverlay, remainingSlots: slotLimit - index };
        return Badge ? <Badge key={type} {...ctx} /> : null;
      })}
    </SlotContainer>
  );
};

export default BadgeSlot;
